import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { workplacePreferencesService, type WorkplaceTaskFilters } from '../services/workplacePreferencesService';

const key = (viewerId: number, subjectId: number) => ['workplace-preferences', viewerId, subjectId];
const defaults: WorkplaceTaskFilters = { role: 'all', period: 'all' };

export function useWorkplaceTaskFilters(viewerId: number, subjectId: number) {
  const client = useQueryClient();
  const ownDashboard = viewerId > 0 && viewerId === subjectId;
  const [temporary, setTemporary] = useState({ viewerId, subjectId, filters: defaults });
  useEffect(() => { setTemporary({ viewerId, subjectId, filters: defaults }); }, [viewerId, subjectId]);
  const query = useQuery({
    queryKey: key(viewerId, viewerId), queryFn: () => workplacePreferencesService.get(),
    enabled: ownDashboard, staleTime: 0, refetchOnMount: 'always', retry: false,
  });
  const save = useMutation({
    mutationFn: (variables: { viewerId: number; subjectId: number; filters: WorkplaceTaskFilters }) =>
      workplacePreferencesService.save(variables.filters),
    onMutate: (variables) => client.cancelQueries({ queryKey: key(variables.viewerId, variables.subjectId) }),
    onSuccess: (data, variables) => { client.setQueryData(key(variables.viewerId, variables.subjectId), data); },
  });
  const filters = ownDashboard ? query.data ?? defaults : temporary.viewerId === viewerId && temporary.subjectId === subjectId ? temporary.filters : defaults;
  return {
    filters, ready: !ownDashboard || query.isSuccess, loading: ownDashboard && query.isPending,
    disabled: ownDashboard && (!query.isSuccess || save.isPending),
    saving: ownDashboard && save.isPending,
    error: !ownDashboard ? null : query.isError ? 'Не удалось загрузить сохранённые фильтры.' : save.isError ? 'Не удалось сохранить фильтр. Повторите выбор.' : null,
    retry: () => { void query.refetch(); },
    change: (changes: Partial<WorkplaceTaskFilters>) => {
      if (!ownDashboard) { setTemporary({ viewerId, subjectId, filters: { ...filters, ...changes } }); return; }
      if (!query.isSuccess || save.isPending) return;
      save.mutate({ viewerId, subjectId, filters: { ...filters, ...changes } });
    },
  };
}
